import { useTheme } from '@emotion/react';
import {
  Button,
  Center,
  Group,
  Loader,
  Paper,
  Progress,
  Skeleton,
  Stack,
  Text,
  createStyles,
} from '@mantine/core';
import { ContestStatus } from '@betterpool/api-types/contests-service';
import { useMediaQuery } from 'react-responsive';
import useTranslation from 'next-translate/useTranslation';

import ContestCountDown from '~/components/utils/ContestCountdown';
import { CurrencyConvertor, ReturnPercentage } from '~/components/utils/formatters';
import { UnstyledNextLink } from '~/components/shared/UnstyledNextLink/UnstyledNextLink';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';
import { getContestThumbnail } from '~/domains/contest/domains/common/utils/getContestThumbnail';
import ContestInvite from '~/domains/contest/domains/invite/ContestInvite';
import useContestInvite from '~/domains/contest/domains/invite/hooks/useContestInvite';
import { Avatar } from '~/domains/common/components/Avatar/Avatar';

import { useContest } from '../../../contest/hooks/useContest';

import ContestRowMenu from './ContestRowMenu';

type ContestRowLoaderProps = {
  isStacked?: boolean;
};

const useStyles = createStyles((theme) => ({
  link: {
    position: 'relative',
    '& a': {
      width: '100%',
      color: theme.black,
      '&:hover': {
        textDecoration: 'none',
      },
    },
  },
}));

export function ContestRowLoader({ isStacked }: ContestRowLoaderProps) {
  if (isStacked) {
    return (
      <Paper p="md">
        <Center>
          <Loader size={100} />
        </Center>
      </Paper>
    );
  }
  return (
    <Paper p="md">
      <Group noWrap spacing="sm" position="apart">
        <Group noWrap>
          <Skeleton width={38} height={38} />
          <Stack spacing={2}>
            <Skeleton width={120} height={16} />
            <Skeleton width={120} height={12} />
          </Stack>
        </Group>
        <Group noWrap>
          <Stack spacing={10} justify="flex-start">
            <Skeleton width={200} height={14} />
            <Skeleton width={200} height={14} />
          </Stack>
        </Group>
      </Group>
    </Paper>
  );
}

type ContestRowProps = {
  contest: Omit<
    Contest,
    | 'entry_deadline_date'
    | 'end_date'
    | 'resizing_mode'
    | 'league'
    | 'unlisted'
    | 'beginnersOnly'
    | 'settings'
    | 'contest_type'
  >;
  showCta?: boolean;
  showMenu?: boolean;
  isStacked?: boolean;
  status?: ContestStatus;
};

function ContestRow({
  contest,
  showCta = true,
  showMenu = false,
  isStacked,
  status = ContestStatus.SCHEDULED,
}: ContestRowProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const showStackedRow = useMediaQuery({ maxWidth: theme.breakpoints.md });
  const { isInviteCTAShown } = useContestInvite();
  const { classes } = useStyles();
  const isLateSwappable = !contest?.is_after_first_late_swap_deadline;

  const showScheduledContestOptions =
    status === ContestStatus.SCHEDULED || (status === ContestStatus.IN_PROGRESS && isLateSwappable);
  const showContestRowMenu = showMenu && showScheduledContestOptions;
  const showInviteCTA = isInviteCTAShown && showScheduledContestOptions;
  const showEnterCTA = showCta && showScheduledContestOptions;
  const showViewCTA = showCta && !showScheduledContestOptions;

  if (!contest) {
    return null;
  }

  if (showStackedRow || isStacked) {
    return (
      <Paper p="md">
        <Stack spacing={12} className={classes.link}>
          <Group noWrap position="apart" align="flex-start">
            <UnstyledNextLink href={`/contest/${contest.id}/detail`}>
              <Group>
                <Avatar
                  radius="xl"
                  variant="filled"
                  size="md"
                  src={getContestThumbnail({
                    contestThumbnailUrl: contest.thumbnail_url,
                    createdByAdmin: contest.created_by_admin,
                    avatarUrl: contest.commissioner.avatarUrl,
                  })}
                  skipNextImageCache
                />
                <Stack spacing={2}>
                  <Text size={16} fw={700}>
                    {contest.name}
                  </Text>
                  {contest.start_date && (
                    <ContestCountDown
                      fontSize={10}
                      contestStart={contest.start_date.toString()}
                      color={theme.colors.dark[2]}
                    />
                  )}
                </Stack>
              </Group>
            </UnstyledNextLink>
            {showContestRowMenu && (
              <ContestRowMenu
                hasEntries={contest.entries.filled > 0}
                contestId={contest.id}
                contestName={contest.name}
              />
            )}
          </Group>

          <UnstyledNextLink href={`/contest/${contest.id}/detail`}>
            <Progress
              sections={[
                {
                  value: ReturnPercentage(contest.entries.filled, contest.entries.max),
                  color: 'primary',
                },
                { value: 100, color: 'gray.2' },
              ]}
            />
            <Group noWrap position="apart">
              <Stack spacing={2} justify="flex-start">
                <Text size={14} fw={700}>
                  {contest.entries.filled}/{contest.entries.max}
                </Text>
                <Text size={10} fw={400} color="dark.2">
                  {t('contest.contestRow.entriesXofMax', {
                    userEntries: contest.entries.user ?? 0,
                    maxPerUser: contest.entries.max_per_user,
                  })}
                </Text>
              </Stack>
              <Stack spacing={2}>
                <Text size={14} fw={700} align="center">
                  {CurrencyConvertor(contest.entry_fee / 100)}
                </Text>
                <Text size={10} fw={400} color="dark.2" align="center">
                  {t('contest.contestRow.entry')}
                </Text>
              </Stack>
              <Stack spacing={2}>
                <Text size={14} fw={700} align="right">
                  {CurrencyConvertor(contest.prize_pool / 100)}
                </Text>
                <Text size={10} fw={400} color="dark.2" align="right">
                  {t('contest.contestRow.prizes')}
                </Text>
              </Stack>
            </Group>
          </UnstyledNextLink>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper p="md">
      <Group spacing="xs" noWrap w="100%" className={classes.link}>
        <UnstyledNextLink href={`/contest/${contest.id}`}>
          <Group noWrap spacing={12} position="apart">
            <Group noWrap>
              <Avatar
                radius="xl"
                variant="filled"
                size="md"
                src={getContestThumbnail({
                  contestThumbnailUrl: contest.thumbnail_url,
                  createdByAdmin: contest.created_by_admin,
                  avatarUrl: contest.commissioner.avatarUrl,
                })}
                skipNextImageCache
              />
              <Stack spacing={2}>
                <Text size={16} fw={700} color="black">
                  {contest.name}
                </Text>
                {contest.start_date && (
                  <ContestCountDown
                    fontSize={10}
                    contestStart={contest.start_date.toString()}
                    color={theme.colors.dark[2]}
                  />
                )}
              </Stack>
            </Group>
            <Group noWrap>
              <Stack spacing={10} justify="flex-start">
                <Text size={14} color="black" fw={700}>
                  {contest.entries.filled} of {contest.entries.max}
                </Text>
                <Progress
                  sections={[
                    {
                      value: ReturnPercentage(contest.entries.filled, contest.entries.max),
                      color: 'primary',
                    },
                    { value: 100, color: 'gray.2' },
                  ]}
                />
              </Stack>
              <Stack spacing={5} align="center">
                <Text size={14} color="black" fw={700}>
                  {CurrencyConvertor(contest.entry_fee / 100)}
                </Text>
                <Text size={10} fw={400} color="dark.2">
                  {t('contest.contestRow.entry')}
                </Text>
              </Stack>
              <Stack spacing={5} align="center">
                <Text size={14} color="black" fw={700}>
                  {CurrencyConvertor(contest.prize_pool / 100)}
                </Text>
                <Text size={10} fw={400} color="dark.2">
                  {t('contest.contestRow.prizes')}
                </Text>
              </Stack>
              {showEnterCTA && (
                <Button>
                  <Text color="black">{t('contest.contestRow.enter')}</Text>
                </Button>
              )}
              {showViewCTA && (
                <Button>
                  <Text color="black">{t('contest.contestRow.view')}</Text>
                </Button>
              )}
            </Group>
          </Group>
        </UnstyledNextLink>
        {showInviteCTA && <ContestInvite contestId={contest?.id} contestName={contest?.name} />}
        {showContestRowMenu && (
          <ContestRowMenu
            hasEntries={contest.entries.filled > 0}
            contestId={contest.id}
            contestName={contest.name}
          />
        )}
      </Group>
    </Paper>
  );
}

type SelfFetchingContestRowProps = {
  contestId: string;
  showCta?: boolean;
  isStacked?: boolean;
};

export function SelfFetchingContestRow({
  contestId,
  showCta,
  isStacked,
}: SelfFetchingContestRowProps) {
  const { data: contest, isLoading } = useContest(contestId);
  return isLoading ? (
    <ContestRowLoader isStacked={isStacked} />
  ) : (
    <ContestRow contest={contest} showCta={showCta} isStacked={isStacked} />
  );
}

export default ContestRow;
